import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class AutoDiscountAfterAdd {
  #isApplying = false;

  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = async (event) => {
    if (this.#isApplying) return;
    if (!(event instanceof Event)) return;

    const detail = /** @type {{data?: {source?: string, didError?: boolean, productId?: string, sections?: Record<string, string>}}} */ (
      event
    ).detail;
    const data = detail?.data;

    if (!data || data.source !== 'product-form-component' || data.didError) return;

    const formComponent = this.#findProductFormComponent(data.productId);
    if (!formComponent) return;

    const discountCode = (formComponent.dataset.discountCode || '').trim();
    if (!discountCode) return;

    if (this.#allCodesAlreadyApplied(discountCode)) return;

    const existingCodes = this.#existingDiscountCodes();
    const requestedCodes = discountCode
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
    const mergedCodes = [...new Set([...existingCodes, ...requestedCodes])];

    if (mergedCodes.length === 0) return;

    const payload = {
      discount: mergedCodes.join(','),
    };

    const sectionIds = Object.keys(data.sections || {});
    if (sectionIds.length > 0) {
      payload.sections = sectionIds;
    }

    this.#isApplying = true;

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify(payload),
        })
      );

      if (!response.ok) return;

      const discountData = await response.json();
      document.dispatchEvent(new DiscountUpdateEvent(discountData, 'auto-discount-after-add'));
    } catch (error) {
      console.error(error);
    } finally {
      this.#isApplying = false;
    }
  };

  #findProductFormComponent(productId) {
    if (!productId) return null;
    return document.querySelector(`product-form-component[data-product-id="${productId}"][data-discount-code]`);
  }

  #existingDiscountCodes() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = document.querySelectorAll('.cart-discount__pill');

    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  #allCodesAlreadyApplied(codesCsv) {
    const existing = this.#existingDiscountCodes();
    const requested = codesCsv
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

    if (requested.length === 0) return true;
    return requested.every((code) => existing.includes(code));
  }
}

new AutoDiscountAfterAdd();
